import React from "react"
import gwLogo from "~/img/goodwatch-logo-white.svg"
import imdbLogo from "~/img/imdb-logo-250.png"
import metacriticLogo from "~/img/metacritic-logo-250.png"
import metacriticLogoIcon from "~/img/metacritic-logo-icon-250.png"
import rottenLogo from "~/img/rotten-logo-250.png"
import rottenLogoIcon from "~/img/rotten-logo-icon-250.png"
import { type AllRatings, scoreLabels } from "~/utils/ratings"
import { useUserData } from "~/routes/api.user-data"
import type { MovieResult, ShowResult } from "~/server/types/details-types"

export interface RatingBadgesProps {
	media: MovieResult | ShowResult
	ratings: AllRatings
	onToggleRate: () => void
}

export default function RatingBadges({
	media,
	ratings,
	onToggleRate,
}: RatingBadgesProps) {
	const { details, mediaType } = media
	const { tmdb_id } = details

	const { data: userData } = useUserData()
	const userScore = userData?.[mediaType]?.[tmdb_id]?.score || null

	const vibeColorIndex = ratings?.goodwatch_overall_score_normalized_percent
		? Math.floor(ratings.goodwatch_overall_score_normalized_percent / 10) * 10
		: null

	const userColorIndex = userScore ? userScore * 10 : null

	return (
		<div className="w-full flex flex-col sm:flex-row items-center sm:justify-between gap-3">
			<div className="w-full sm:w-auto flex items-center justify-between gap-4 lg:gap-8">
				<dl
					className={`${ratings?.goodwatch_overall_score_normalized_percent ? "" : "opacity-60"} flex items-center gap-2`}
				>
					<img
						className={`block h-9 p-1.5 rounded-full shadow-xl ${vibeColorIndex == null ? "bg-gray-950" : `bg-vibe-${vibeColorIndex}`}`}
						src={gwLogo}
						alt="GoodWatch Logo"
					/>
					<dd className="text-base">
						{ratings?.goodwatch_overall_score_normalized_percent ? (
							<>
								<span
									className={`text-xl font-semibold ${vibeColorIndex == null ? "text-gray-300" : `text-vibe-${vibeColorIndex}`}`}
								>
									{Math.floor(
										ratings?.goodwatch_overall_score_normalized_percent,
									)}
								</span>
								<span className="text-gray-300 font-normal text-sm">/100</span>
							</>
						) : (
							"-"
						)}
					</dd>
				</dl>

				<div
					className="
						flex items-center gap-2 p-2
						bg-gray-700/70 hover:bg-gray-700
						rounded-lg border border-transparent hover:border-gray-600/50
						cursor-pointer
					"
					onClick={onToggleRate}
					onKeyDown={() => {}}
				>
					{userScore && (
						<div
							className={`
										flex items-center justify-center
										w-8 h-8
										rounded-full ${userColorIndex == null ? "bg-gray-950" : `bg-vibe-${userColorIndex}`} 
										text-base font-semibold text-center text-white
									`}
						>
							<span className="w-full">{userScore}</span>
						</div>
					)}
					<div className="flex flex-col text-sm">
						<span className="text-gray-300 font-light text-xs">
							My Score
						</span>
						<span
							className={`
								${userColorIndex == null ? "text-gray-200" : `text-vibe-${userColorIndex}`} 
								font-semibold text-center
							`}
						>
							{userScore ? scoreLabels[userScore] : scoreLabels[0]}
						</span>
					</div>
				</div>
			</div>

			<div className="w-full sm:w-auto flex items-center justify-between gap-2 sm:gap-3">
				<a
					className="flex-1 min-w-0"
					href={ratings?.imdb_url}
					target="_blank"
					rel="noreferrer"
				>
					<dl
						className={`
							${ratings?.imdb_url ? "hover:border-white/[.45] active:border-white/[.45]" : "opacity-60"}
							h-full py-1.5 px-2
							flex flex-col items-center gap-1
							bg-imdb shadow-xl overflow-hidden rounded-md
							text-center brightness-[.8] hover:brightness-100
						`}
					>
						<img
							className="block h-5 object-contain"
							src={imdbLogo}
							alt="IMDb Logo"
						/>
						<dd className="text-base font-semibold tracking-tight text-gray-900">
							{ratings?.imdb_user_score_original
								? ratings?.imdb_user_score_original.toFixed(1)
								: "–"}
						</dd>
					</dl>
				</a>

				<a
					className="flex-1 min-w-0"
					href={ratings?.metacritic_url}
					target="_blank"
					rel="noreferrer"
				>
					<dl
						className={`
							${ratings?.metacritic_url ? "hover:border-black/[.45] active:border-black/[.45]" : "opacity-60"}
							h-full py-1.5 px-2
							flex flex-col items-center gap-1
							bg-metacritic shadow-xl overflow-hidden rounded-md
							text-center brightness-[.8] hover:brightness-100
						`}
					>
						<img
							className="block h-5 object-contain"
							src={metacriticLogo}
							alt="Metacritic Logo"
						/>
						<dd className="flex gap-1 text-base font-semibold tracking-tight text-gray-100">
							<span>
								{ratings?.metacritic_meta_score_original
									? Math.floor(ratings?.metacritic_meta_score_original)
									: "–"}
							</span>
							<span className="text-sm font-light">|</span>
							<span>
								{ratings?.metacritic_user_score_original
									? ratings?.metacritic_user_score_original.toFixed(1)
									: "–"}
							</span>
						</dd>
					</dl>
				</a>

				<a
					className="flex-1 min-w-0"
					href={ratings?.rotten_tomatoes_url}
					target="_blank"
					rel="noreferrer"
				>
					<dl
						className={`
							${ratings?.rotten_tomatoes_url ? "hover:border-white/[.45] active:border-white/[.45]" : "opacity-60"}
							h-full py-1.5 px-2
							flex flex-col items-center gap-1
							bg-rotten shadow-xl overflow-hidden rounded-md
							text-center brightness-[0.8] hover:brightness-100
						`}
					>
						<img
							className="block h-5 object-contain"
							src={rottenLogo}
							alt="Rotten Tomatoes Logo"
						/>
						<dd className="flex gap-1 text-base font-semibold tracking-tight text-gray-50">
							<span>
								{ratings?.rotten_tomatoes_tomato_score_original
									? Math.floor(ratings?.rotten_tomatoes_tomato_score_original)
									: "–"}
							</span>
							<span className="text-sm font-light">|</span>
							<span>
								{ratings?.rotten_tomatoes_audience_score_original
									? Math.floor(ratings?.rotten_tomatoes_audience_score_original)
									: "–"}
							</span>
						</dd>
					</dl>
				</a>
			</div>
		</div>
	)
}
